import React, { useState } from 'react'
import {
  Brain, Send, CheckCircle, XCircle, AlertTriangle, Lightbulb,
  ArrowRight, History, ChevronDown, ChevronUp, Star, Target,
  BarChart2, BookOpen, RefreshCw, Award, Repeat
} from 'lucide-react'
import MisconceptionAlert from '../components/MisconceptionAlert'
import OntologyTag from '../components/OntologyTag'
import { useProgressStore } from '../store/progressStore'
import type { TutorAnalysisResult } from '../types'

// ────────────────────────────────────────────────────────────────────────────
// 유리수와 순환소수 데모 시나리오 (NM01~NM08)
// ────────────────────────────────────────────────────────────────────────────
const DEMO_SCENARIOS = [
  {
    label: '📌 시나리오 1: 순환소수를 유한소수로 오해 (NM01)',
    emoji: '📌',
    category: '소수 분류',
    step: 'NR01',
    question: '0.333...은 유한소수인가요, 무한소수인가요?',
    answer: '유한소수입니다. 3이 반복되니까 끝이 있어요.',
    wrongAnswer: '유한소수입니다. 3이 반복되니까 끝이 있어요.',
    note: '순환소수를 유한소수로 착각한 경우',
  },
  {
    label: '📌 시나리오 2: 순환마디 표기 오류 (NM03)',
    emoji: '📌',
    category: '점 표기법',
    step: 'NR02',
    question: '0.131313...을 점 표기법으로 나타내시오.',
    answer: '0.1̇3 (끝 자리 3 위에만 점)',
    wrongAnswer: '0.1̇3 (끝 자리 3 위에만 점)',
    note: '순환마디 첫 자리에도 점을 찍어야 함을 모르는 경우',
  },
  {
    label: '📌 시나리오 3: 분수 변환 배수 오류 (NM05)',
    emoji: '📌',
    category: '분수 변환',
    step: 'NR03',
    question: '0.27̇을 분수로 나타내시오. (순환마디: 7)',
    answer: '100x - x = 99x로 계산해서 27/99',
    wrongAnswer: '100x - x = 99x로 계산해서 27/99',
    note: '순환 전 자리(2)를 고려하지 않고 잘못 계산한 경우',
  },
  {
    label: '📌 시나리오 4: 기약분수 생략 후 판별 (NM08)',
    emoji: '📌',
    category: '유한소수 판별',
    step: 'NR04',
    question: '4/6이 유한소수인지 순환소수인지 판별하시오.',
    answer: '분모 6 = 2×3이므로 3이 있어서 순환소수입니다.',
    wrongAnswer: '분모 6 = 2×3이므로 3이 있어서 순환소수입니다.',
    note: '기약분수로 먼저 변환하지 않은 경우 (결과는 같지만 과정이 틀림)',
  },
  {
    label: '📌 시나리오 5: 기약분수 판별 오류 (NM07)',
    emoji: '📌',
    category: '유한소수 판별',
    step: 'NR04',
    question: '3/15가 유한소수인지 순환소수인지 판별하시오.',
    answer: '분모 15 = 3×5이므로 3이 있어서 순환소수입니다.',
    wrongAnswer: '분모 15 = 3×5이므로 3이 있어서 순환소수입니다.',
    note: '기약분수(1/5)로 먼저 바꾸면 분모가 5뿐 → 유한소수임을 모르는 경우',
  },
  {
    label: '✅ 시나리오 6: 올바른 순환소수 변환',
    emoji: '✅',
    category: '분수 변환',
    step: 'NR03',
    question: '0.4̇2̇를 분수로 나타내시오.',
    answer: 'x = 0.4242..., 100x = 42.4242..., 99x = 42, x = 42/99 = 14/33',
    wrongAnswer: 'x = 0.4242..., 100x = 42.4242..., 99x = 42, x = 42/99 = 14/33',
    note: '정확한 답변',
  },
]

const SCENARIO_KEYS = [
  'NR01-wrong-nm01', 'NR02-wrong-nm03', 'NR03-wrong-nm05',
  'NR04-wrong-nm08', 'NR04-wrong-nm07', 'NR03-correct',
]

// ────────────────────────────────────────────────────────────────────────────
// 데모 결과 데이터
// ────────────────────────────────────────────────────────────────────────────
const DEMO_RESULTS: Record<string, TutorAnalysisResult> = {
  'NR01-wrong-nm01': {
    student_id: 'student_001', current_step: 'NR01', step_concept: '유한소수와 무한소수', score: 15,
    mastery: { step_id: 'NR01', score: 15, attempts: 1, mastered: false, near_mastery: false, level: 'learning', message: '순환소수는 무한소수입니다. 다시 학습이 필요해요.' },
    misconceptions: [{
      id: 'NM01', type: 'RECURRING_IS_FINITE', severity: 'high',
      description: 'Treating repeating decimal as finite decimal',
      description_ko: '순환소수를 유한소수로 착각하는 오류',
      feedback_id: 'F-NM01', learning_step: 'NR01',
    }],
    concept_gaps: [{ concept: 'FiniteDecimal', from_misconception: 'NM01', severity: 'high', remedial_step: 'NR01' }],
    feedbacks: [{
      id: 'F-NM01', misconception: 'NM01',
      title: '순환소수는 무한소수입니다!',
      message: '0.333...은 소수점 아래가 끝없이 계속되므로 무한소수입니다. "3이 반복된다"는 것은 끝이 있다는 뜻이 아니라, 무한히 반복된다는 뜻이에요. 유한소수는 0.75처럼 소수점 아래가 완전히 끝나는 소수입니다.',
      hint: '유한소수: 소수점 아래가 "끝남". 순환소수: 소수점 아래가 "무한히 반복"',
      next_step: 'NR01 유한·무한소수 개념 재학습',
    }],
    primary_feedback: {
      id: 'F-NM01', misconception: 'NM01',
      title: '순환소수 ≠ 유한소수!',
      message: '0.333...은 무한소수입니다. 반복 패턴이 있다고 해서 끝이 있는 게 아니에요. 순환소수는 "반복 패턴이 있는 무한소수"입니다. 유한소수(0.75)와 혼동하지 마세요!',
      hint: '유한소수: 0.75처럼 딱 끝남 / 순환소수: 0.333...처럼 무한히 반복',
      next_step: 'NR01 재학습',
    },
    next_step_recommendation: {
      current_step: 'NR01', next_step: 'NR01',
      recommendation: 'reinforce', reason: 'NM01 오개념 해결 후 유한·무한소수 개념 재학습', score: 15, misconception_count: 1,
    },
    ontology_classes_activated: ['FiniteDecimal', 'InfiniteDecimal', 'RecurringDecimal', 'Misconception', 'Feedback'],
    agent_reasoning: {
      perceived: '학생이 0.333...을 유한소수라고 답했습니다.',
      reasoned: 'NM01(순환소수를 유한소수로 오인) 오개념이 감지됩니다.',
      planned: 'NM01 피드백으로 유한·무한소수의 차이를 명확히 설명',
      acted: '유한소수(끝남)와 순환소수(무한반복)의 차이를 구체적 예시로 이해시킵니다.',
    },
  },

  'NR02-wrong-nm03': {
    student_id: 'student_001', current_step: 'NR02', step_concept: '순환소수의 표현', score: 20,
    mastery: { step_id: 'NR02', score: 20, attempts: 1, mastered: false, near_mastery: false, level: 'learning', message: '점 표기법의 규칙을 다시 확인하세요.' },
    misconceptions: [{
      id: 'NM03', type: 'DOT_NOTATION_ERROR', severity: 'medium',
      description: 'Only marking the last digit of the repeating block',
      description_ko: '순환마디 끝 자리에만 점을 찍는 오류',
      feedback_id: 'F-NM03', learning_step: 'NR02',
    }],
    concept_gaps: [{ concept: 'RecurringDecimalNotation', from_misconception: 'NM03', severity: 'medium', remedial_step: 'NR02' }],
    feedbacks: [{
      id: 'F-NM03', misconception: 'NM03',
      title: '점은 순환마디 첫 자리와 끝 자리 모두에!',
      message: '0.131313...의 순환마디는 13입니다. 점 표기법 규칙: 순환마디의 첫 번째 숫자(1) 위와 마지막 숫자(3) 위, 두 군데에 점을 찍습니다. 따라서 0.1̇3̇이 올바른 표기입니다.',
      hint: '순환마디 13 → 1위에 점, 3위에 점 → 0.1̇3̇',
      next_step: 'NR02 점 표기법 재학습',
    }],
    primary_feedback: {
      id: 'F-NM03', misconception: 'NM03',
      title: '0.131313... = 0.1̇3̇ (첫 자리와 끝 자리 모두!)',
      message: '점 표기법: 순환마디의 첫 번째 숫자와 마지막 숫자 위에 각각 점. 순환마디가 1자리면 그 위에 점 1개. 2자리 이상이면 양 끝에 점 2개.',
      hint: '순환마디 1자리: 점 1개. 2자리 이상: 양 끝에 점 2개.',
      next_step: 'NR02 재학습',
    },
    next_step_recommendation: {
      current_step: 'NR02', next_step: 'NR02',
      recommendation: 'reinforce', reason: 'NM03 오개념 해결 후 점 표기법 재학습', score: 20, misconception_count: 1,
    },
    ontology_classes_activated: ['RecurringDecimalNotation', 'RepetendBlock', 'DotNotation', 'Misconception', 'Feedback'],
    agent_reasoning: {
      perceived: '학생이 0.1̇3 (끝 자리에만 점)으로 표기했습니다.',
      reasoned: 'NM03(점 표기 오류) 오개념이 감지됩니다.',
      planned: 'NM03 피드백으로 순환마디 양 끝에 점을 찍는 규칙 설명',
      acted: '순환마디 첫 자리와 끝 자리에 각각 점을 찍는 이유를 예시로 설명합니다.',
    },
  },

  'NR03-wrong-nm05': {
    student_id: 'student_001', current_step: 'NR03', step_concept: '순환소수를 분수로 변환', score: 20,
    mastery: { step_id: 'NR03', score: 20, attempts: 1, mastered: false, near_mastery: false, level: 'learning', message: '순환 전 자리를 고려한 변환 방법을 다시 학습하세요.' },
    misconceptions: [{
      id: 'NM05', type: 'PARTIAL_RECURRING_ERROR', severity: 'high',
      description: 'Not accounting for non-repeating digits before the recurring block',
      description_ko: '순환 전 자리를 고려하지 않는 오류',
      feedback_id: 'F-NM05', learning_step: 'NR03',
    }],
    concept_gaps: [{ concept: 'RecurringToFraction', from_misconception: 'NM05', severity: 'high', remedial_step: 'NR03' }],
    feedbacks: [{
      id: 'F-NM05', misconception: 'NM05',
      title: '순환 전 자리가 있으면 두 가지 곱셈이 필요!',
      message: '0.27̇에서 순환마디는 7 (1자리)이고, 앞의 2는 순환하지 않습니다. 이 경우 10x = 2.777..., 100x = 27.777... 을 이용해야 합니다. 100x - 10x = 25 → 90x = 25 → x = 25/90 = 5/18.',
      hint: '순환 전 n자리, 순환마디 m자리 → 10^(n+m)x - 10^n x',
      next_step: 'NR03 혼합형 변환 재학습',
    }],
    primary_feedback: {
      id: 'F-NM05', misconception: 'NM05',
      title: '0.27̇ = 5/18 (순환 전 자리 주의!)',
      message: 'x = 0.2777...\n10x = 2.777...\n100x = 27.777...\n100x - 10x = 25 → 90x = 25 → x = 25/90 = 5/18',
      hint: '순환 전 자리가 있을 때: 두 가지 곱셈 후 빼기',
      next_step: 'NR03 재학습',
    },
    next_step_recommendation: {
      current_step: 'NR03', next_step: 'NR03',
      recommendation: 'reinforce', reason: 'NM05 오개념 해결 후 혼합형 순환소수 변환 재학습', score: 20, misconception_count: 1,
    },
    ontology_classes_activated: ['RecurringToFraction', 'PartialRecurring', 'Equation', 'Misconception', 'Feedback'],
    agent_reasoning: {
      perceived: '학생이 0.27̇을 27/99로 잘못 계산했습니다.',
      reasoned: 'NM05(순환 전 자리 미고려) 오개념이 감지됩니다.',
      planned: 'NM05 피드백으로 10x와 100x를 활용한 정확한 방법 설명',
      acted: '단계별 계산 과정으로 5/18이 나오는 이유를 명확히 설명합니다.',
    },
  },

  'NR04-wrong-nm08': {
    student_id: 'student_001', current_step: 'NR04', step_concept: '유리수와 소수의 관계', score: 40,
    mastery: { step_id: 'NR04', score: 40, attempts: 1, mastered: false, near_mastery: false, level: 'learning', message: '기약분수로 먼저 변환하는 과정이 필요합니다.' },
    misconceptions: [{
      id: 'NM08', type: 'SKIP_SIMPLIFICATION', severity: 'medium',
      description: 'Judging finite/recurring without simplifying to lowest terms first',
      description_ko: '기약분수화 없이 바로 분모 소인수 판별하는 오류',
      feedback_id: 'F-NM08', learning_step: 'NR04',
    }],
    concept_gaps: [{ concept: 'FiniteDecimalJudgement', from_misconception: 'NM08', severity: 'medium', remedial_step: 'NR04' }],
    feedbacks: [{
      id: 'F-NM08', misconception: 'NM08',
      title: '반드시 기약분수로 만든 후 판별하세요!',
      message: '4/6의 분모 6 = 2×3이지만, 기약분수로 먼저 만들면 4/6 = 2/3. 분모 3은 2, 5 외의 소인수이므로 순환소수가 됩니다. 결과는 같지만, 기약분수화가 필수 과정입니다. (기약분수화 생략 시 오답 가능!)',
      hint: '판별 순서: 기약분수 변환 → 분모 소인수분해 → 2, 5만 있으면 유한소수',
      next_step: 'NR04 유한소수 판별 재학습',
    }],
    primary_feedback: {
      id: 'F-NM08', misconception: 'NM08',
      title: '기약분수 먼저, 그 다음 소인수 판별!',
      message: '4/6 → 기약: 2/3 → 분모 3 → 2, 5 외 소인수 있음 → 순환소수. 기약분수화 없이 판별하면 오답이 나올 수 있어요!',
      hint: '예: 6/15 → 기약 2/5 → 분모 5뿐 → 유한소수 (기약 안 하면 틀릴 수 있음!)',
      next_step: 'NR04 재학습',
    },
    next_step_recommendation: {
      current_step: 'NR04', next_step: 'NR04',
      recommendation: 'reinforce', reason: 'NM08 오개념 해결 후 기약분수 판별법 재학습', score: 40, misconception_count: 1,
    },
    ontology_classes_activated: ['FiniteDecimalJudgement', 'LowestTerms', 'PrimeFactorization', 'Misconception', 'Feedback'],
    agent_reasoning: {
      perceived: '학생이 기약분수 변환 없이 바로 분모 소인수를 판별했습니다.',
      reasoned: 'NM08(기약분수화 생략) 오개념이 감지됩니다.',
      planned: 'NM08 피드백으로 기약분수화의 필요성 강조',
      acted: '기약분수화 후 판별하는 정확한 순서를 단계별로 보여줍니다.',
    },
  },

  'NR04-wrong-nm07': {
    student_id: 'student_001', current_step: 'NR04', step_concept: '유리수와 소수의 관계', score: 20,
    mastery: { step_id: 'NR04', score: 20, attempts: 1, mastered: false, near_mastery: false, level: 'learning', message: '기약분수화 후 판별해야 올바른 결과가 나옵니다.' },
    misconceptions: [{
      id: 'NM07', type: 'FINITE_DECIMAL_JUDGEMENT_ERROR', severity: 'high',
      description: 'Incorrectly judging finite/recurring decimal without simplifying',
      description_ko: '기약분수 없이 유한/순환소수를 잘못 판별하는 오류',
      feedback_id: 'F-NM07', learning_step: 'NR04',
    }],
    concept_gaps: [{ concept: 'FiniteDecimalJudgement', from_misconception: 'NM07', severity: 'high', remedial_step: 'NR04' }],
    feedbacks: [{
      id: 'F-NM07', misconception: 'NM07',
      title: '3/15 → 기약분수 1/5 → 유한소수!',
      message: '3/15를 기약분수로 만들면 1/5입니다. 분모 5 = 5¹ → 소인수 5뿐 → 유한소수. 3/15 자체의 분모 15 = 3×5로 보면 순환소수처럼 보이지만, 반드시 기약분수화 후 판별해야 합니다!',
      hint: '3/15 → GCD(3,15)=3으로 약분 → 1/5 → 분모 5뿐 → 유한소수 (0.2)',
      next_step: 'NR04 기약분수 판별 재학습',
    }],
    primary_feedback: {
      id: 'F-NM07', misconception: 'NM07',
      title: '3/15 = 1/5 → 유한소수 0.2!',
      message: '3/15에서 GCD(3,15)=3이므로 기약분수: 1/5. 분모 5는 소인수 5뿐 → 유한소수. 1/5 = 0.2 (유한소수) ✓',
      hint: '기약분수화 필수! 3/15 → 1/5 → 유한소수',
      next_step: 'NR04 재학습',
    },
    next_step_recommendation: {
      current_step: 'NR04', next_step: 'NR04',
      recommendation: 'reinforce', reason: 'NM07 오개념 해결 후 기약분수→소인수 판별 재학습', score: 20, misconception_count: 1,
    },
    ontology_classes_activated: ['FiniteDecimalJudgement', 'LowestTerms', 'PrimeFactor', 'Misconception', 'Feedback'],
    agent_reasoning: {
      perceived: '학생이 3/15를 순환소수로 잘못 판별했습니다.',
      reasoned: 'NM07(기약분수 없이 판별 오류) 오개념이 강하게 감지됩니다.',
      planned: 'NM07 피드백으로 기약분수 1/5 → 유한소수 과정 단계별 설명',
      acted: 'GCD로 기약분수 만든 후 분모 소인수 확인하는 정확한 과정을 보여줍니다.',
    },
  },

  'NR03-correct': {
    student_id: 'student_001', current_step: 'NR03', step_concept: '순환소수를 분수로 변환', score: 100,
    mastery: { step_id: 'NR03', score: 100, attempts: 1, mastered: true, near_mastery: false, level: 'mastered', message: '완벽합니다! 순환소수 변환을 마스터했습니다.' },
    misconceptions: [],
    concept_gaps: [],
    feedbacks: [{
      id: 'F-correct', type: 'encouragement',
      title: '훌륭해요! 🎉',
      message: '0.4̇2̇ = 14/33을 정확히 계산했습니다! x를 이용한 방정식 풀이와 기약분수화까지 완벽하게 처리했어요.',
      emoji: '🌟',
      personalized_message: '완벽한 답변입니다! 유한소수 판별 단계로 도전해보세요.',
    }],
    primary_feedback: {
      id: 'F-correct', type: 'encouragement',
      title: '훌륭해요! 🎉',
      message: '0.4̇2̇ = 42/99 = 14/33 완벽! 순환마디 2자리(42) → 100배 → 99x = 42 → 기약분수 14/33.',
      emoji: '🌟',
    },
    next_step_recommendation: {
      current_step: 'NR03', next_step: 'NR04',
      recommendation: 'advance', reason: '순환소수 변환 마스터! 유한소수 판별 학습으로 진행', score: 100, misconception_count: 0,
    },
    ontology_classes_activated: ['RecurringToFraction', 'LowestTerms', 'Equation', 'Feedback'],
    agent_reasoning: {
      perceived: '학생이 0.4̇2̇ = 14/33을 정확하게 계산했습니다.',
      reasoned: '오개념이 없습니다. 완전 학습 상태입니다.',
      planned: '격려 피드백 후 NR04 유한소수 판별로 진행',
      acted: '칭찬과 함께 다음 단계를 안내합니다.',
    },
  },
}

// ────────────────────────────────────────────────────────────────────────────
// 세션 히스토리 타입
// ────────────────────────────────────────────────────────────────────────────
interface HistoryEntry {
  scenarioLabel: string
  score: number
  grade: string
  misconceptionCount: number
  at: string
}

function getGradeFromScore(score: number): string {
  if (score >= 90) return 'A'
  if (score >= 70) return 'B'
  if (score >= 50) return 'C'
  return 'D'
}

const STEP_COLORS: Record<string, string> = {
  NR01: 'bg-blue-100 text-blue-700',
  NR02: 'bg-purple-100 text-purple-700',
  NR03: 'bg-green-100 text-green-700',
  NR04: 'bg-orange-100 text-orange-700',
  NR05: 'bg-red-100 text-red-700',
  NR06: 'bg-pink-100 text-pink-700',
}

// ────────────────────────────────────────────────────────────────────────────
// 컴포넌트
// ────────────────────────────────────────────────────────────────────────────
export default function N3TutorPage() {
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [customAnswer, setCustomAnswer] = useState('')
  const [result, setResult] = useState<TutorAnalysisResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [showHistory, setShowHistory] = useState(false)
  const [showReasoning, setShowReasoning] = useState(false)
  const [showOntology, setShowOntology] = useState(false)

  const { recordMisconceptions } = useProgressStore(s => ({
    recordMisconceptions: s.recordMisconceptions,
  }))

  const scenario = DEMO_SCENARIOS[selectedIdx]

  const handleAnalyze = async () => {
    setLoading(true)
    setResult(null)
    await new Promise(r => setTimeout(r, 900))
    const key = SCENARIO_KEYS[selectedIdx]
    const res = DEMO_RESULTS[key]
    setResult(res)

    if (res.misconceptions.length > 0) {
      recordMisconceptions('N2-1', res.misconceptions.map(m => m.id))
    }

    setHistory(prev => [
      {
        scenarioLabel: scenario.label,
        score: res.score,
        grade: getGradeFromScore(res.score),
        misconceptionCount: res.misconceptions.length,
        at: new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }),
      },
      ...prev,
    ].slice(0, 10))

    setLoading(false)
  }

  const recColor =
    result?.next_step_recommendation.recommendation === 'advance'
      ? 'text-emerald-700 bg-emerald-50 border border-emerald-200'
      : result?.next_step_recommendation.recommendation === 'reinforce'
        ? 'text-blue-700 bg-blue-50 border border-blue-200'
        : 'text-orange-700 bg-orange-50 border border-orange-200'

  const avgScore = history.length > 0
    ? Math.round(history.reduce((s, h) => s + h.score, 0) / history.length)
    : null

  return (
    <div className="p-6 space-y-5">
      {/* 헤더 */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
            <Brain size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              AI 튜터 — 유리수와 순환소수
              <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-semibold">N2-1</span>
            </h1>
            <p className="text-sm text-slate-500">순환소수 단원의 오개념을 감지하고 맞춤 피드백을 제공합니다</p>
          </div>
        </div>
        {avgScore !== null && (
          <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 px-3 py-1.5 rounded-xl">
            <Star size={13} className="text-teal-500" />
            <span className="text-xs font-bold text-teal-700">세션 평균 {avgScore}점</span>
          </div>
        )}
      </div>

      {/* 배너 */}
      <div className="rounded-2xl bg-gradient-to-r from-teal-600 to-cyan-700 text-white p-4">
        <div className="flex items-center gap-2 mb-2">
          <Repeat size={16} />
          <span className="font-bold text-sm">순환소수 단원 주요 오개념</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { code: 'NM01', label: '순환소수=무한소수', step: 'NR01' },
            { code: 'NM03', label: '점 표기 양 끝', step: 'NR02' },
            { code: 'NM05', label: '순환 전 자리 처리', step: 'NR03' },
            { code: 'NM07', label: '기약분수 후 판별', step: 'NR04' },
            { code: 'NM08', label: '기약분수화 필수', step: 'NR04' },
          ].map(m => (
            <span key={m.code} className="text-xs bg-white/20 px-2 py-1 rounded-lg font-medium">
              {m.code}: {m.label}
            </span>
          ))}
        </div>
      </div>

      {/* 세션 기록 */}
      {history.length > 0 && (
        <div className="card border border-slate-200">
          <button onClick={() => setShowHistory(!showHistory)}
            className="flex items-center justify-between w-full text-sm font-semibold text-slate-700 hover:text-teal-600 transition-colors">
            <span className="flex items-center gap-2"><History size={14} />세션 기록 ({history.length}회)</span>
            {showHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {showHistory && (
            <div className="mt-3 space-y-1.5">
              {history.map((h, i) => (
                <div key={i} className="flex items-center gap-3 text-xs py-1 border-b border-slate-50 last:border-0">
                  <span className="text-slate-400 w-10">{h.at}</span>
                  <span className="flex-1 text-slate-600 truncate">{h.scenarioLabel.replace(/^📌|✅\s/, '')}</span>
                  <span className={`font-black w-12 text-right ${h.score >= 70 ? 'text-emerald-600' : h.score >= 40 ? 'text-orange-600' : 'text-red-600'}`}>
                    {h.score}점
                  </span>
                  <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${h.score >= 70 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {h.grade}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 2열 레이아웃 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 왼쪽: 시나리오 선택 + 답안 */}
        <div className="space-y-4">
          <div className="card">
            <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
              <Target size={14} className="text-teal-500" />
              순환소수 오개념 시나리오
              <span className="ml-auto text-xs text-slate-400 font-normal">({DEMO_SCENARIOS.length}가지)</span>
            </p>
            <div className="space-y-1.5">
              {DEMO_SCENARIOS.map((s, i) => (
                <button key={i} onClick={() => { setSelectedIdx(i); setResult(null); setCustomAnswer('') }}
                  className={`w-full text-left px-3 py-2.5 rounded-xl text-xs transition-all flex items-start gap-2 ${
                    selectedIdx === i ? 'bg-teal-100 text-teal-700 font-semibold shadow-sm' : 'hover:bg-slate-50 text-slate-600'
                  }`}>
                  <span className="text-base leading-none mt-0.5">{s.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{s.category}</div>
                    <div className="text-slate-400 font-normal mt-0.5 truncate">{s.note}</div>
                  </div>
                  <span className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded-md font-bold ${STEP_COLORS[s.step] || 'bg-slate-100 text-slate-600'}`}>
                    {s.step}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="card space-y-3">
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1">
                <BookOpen size={11} /> 문제
                <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-md font-bold ${STEP_COLORS[scenario.step] || 'bg-slate-100 text-slate-600'}`}>
                  {scenario.step}
                </span>
              </p>
              <p className="text-sm text-slate-800 bg-slate-50 rounded-xl p-3 border border-slate-200 leading-relaxed">
                {scenario.question}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-1">학생 답안 (수정 가능)</p>
              <textarea
                className="w-full h-20 resize-none border border-slate-200 rounded-xl p-3 text-sm text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all"
                value={customAnswer || scenario.wrongAnswer}
                onChange={e => setCustomAnswer(e.target.value)}
                placeholder="답안을 입력하세요..."
              />
              <p className="text-xs text-slate-400 mt-1">💡 답안을 직접 수정해보면 다양한 오개념을 실험할 수 있습니다.</p>
            </div>
            <button onClick={handleAnalyze} disabled={loading}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading
                ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> AI 분석 중...</>
                : <><Send size={16} /> AI 튜터 분석 시작</>}
            </button>
          </div>
        </div>

        {/* 오른쪽: 분석 결과 */}
        <div className="space-y-4">
          {!result && !loading && (
            <div className="card flex flex-col items-center justify-center h-80 text-center bg-gradient-to-b from-teal-50 to-white">
              <div className="w-16 h-16 rounded-2xl bg-teal-100 flex items-center justify-center mb-4">
                <Repeat size={32} className="text-teal-400" />
              </div>
              <p className="text-slate-500 text-sm font-semibold">순환소수 시나리오를 선택하고</p>
              <p className="text-slate-400 text-sm">"AI 튜터 분석 시작"을 눌러보세요</p>
              <div className="mt-4 text-xs text-slate-300">NM01~NM08 오개념 감지 · 단계별 피드백 · 다음 학습 추천</div>
            </div>
          )}

          {loading && (
            <div className="card flex flex-col items-center justify-center h-80 text-center">
              <div className="relative mb-4">
                <div className="w-16 h-16 border-4 border-teal-100 border-t-teal-500 rounded-full animate-spin" />
                <Brain size={20} className="absolute inset-0 m-auto text-teal-400" />
              </div>
              <p className="text-slate-600 font-semibold">AI 분석 중...</p>
              <p className="text-slate-400 text-xs mt-1">순환소수 오개념 패턴 매칭 중...</p>
            </div>
          )}

          {result && (
            <>
              {/* 점수 카드 */}
              <div className={`card flex items-center gap-4 ${result.score >= 70 ? 'bg-emerald-50 border border-emerald-200' : result.score >= 40 ? 'bg-orange-50 border border-orange-200' : 'bg-red-50 border border-red-200'}`}>
                {result.score >= 70
                  ? <CheckCircle size={36} className="text-emerald-500 flex-shrink-0" />
                  : <XCircle size={36} className="text-red-500 flex-shrink-0" />}
                <div className="flex-1">
                  <div className="text-3xl font-black text-slate-800">{result.score}점</div>
                  <div className="text-sm text-slate-600 mt-0.5">{result.mastery.message}</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                      result.mastery.mastered ? 'bg-emerald-100 text-emerald-700' :
                      result.mastery.near_mastery ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {result.mastery.level === 'mastered' ? '✓ 마스터' : result.mastery.level === 'near_mastery' ? '거의 완료' : '학습 중'}
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-md font-bold ${STEP_COLORS[result.current_step] || 'bg-slate-100 text-slate-600'}`}>
                      {result.current_step}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-black ${result.score >= 70 ? 'text-emerald-500' : result.score >= 40 ? 'text-orange-500' : 'text-red-500'}`}>
                    {getGradeFromScore(result.score)}
                  </div>
                  <div className="text-xs text-slate-400">등급</div>
                </div>
              </div>

              {/* 오개념 */}
              {result.misconceptions.length > 0 && (
                <div className="card">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle size={15} className="text-orange-500" />
                    <p className="text-sm font-bold text-slate-700">감지된 오개념</p>
                    <span className="ml-auto text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-bold">
                      {result.misconceptions.length}건
                    </span>
                  </div>
                  <div className="space-y-3">
                    {result.misconceptions.map(m => (
                      <MisconceptionAlert key={m.id} misconception={m}
                        feedback={result.feedbacks.find(f => f.id === m.feedback_id)} />
                    ))}
                  </div>
                </div>
              )}

              {/* 주요 피드백 */}
              {result.primary_feedback && (
                <div className="card bg-amber-50 border border-amber-200">
                  <div className="flex items-start gap-2">
                    <Lightbulb size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-slate-800">{result.primary_feedback.title}</p>
                      <p className="text-sm text-slate-700 mt-1 leading-relaxed whitespace-pre-line">{result.primary_feedback.message}</p>
                      {result.primary_feedback.hint && (
                        <div className="mt-2 text-xs text-amber-800 bg-amber-100 rounded-xl px-3 py-2 border border-amber-200">
                          💡 <span className="font-semibold">힌트:</span> {result.primary_feedback.hint}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* 다음 학습 추천 */}
              <div className={`card flex items-center gap-3 ${recColor}`}>
                <ArrowRight size={18} className="flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wide opacity-70">다음 학습 단계 추천</p>
                  <p className="text-sm font-bold">{result.next_step_recommendation.next_step}</p>
                  <p className="text-xs opacity-80 mt-0.5">{result.next_step_recommendation.reason}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-lg font-bold ${
                  result.next_step_recommendation.recommendation === 'advance' ? 'bg-emerald-200 text-emerald-800' : 'bg-blue-200 text-blue-800'
                }`}>
                  {result.next_step_recommendation.recommendation === 'advance' ? '↑ 진행' : '↩ 복습'}
                </span>
              </div>

              {/* Agentic AI 추론 */}
              <div className="card">
                <button onClick={() => setShowReasoning(!showReasoning)}
                  className="flex items-center justify-between w-full text-sm font-semibold text-slate-700 hover:text-teal-600 transition-colors">
                  <span className="flex items-center gap-2"><BarChart2 size={14} />🤖 Agentic AI 추론 과정</span>
                  {showReasoning ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </button>
                {showReasoning && (
                  <div className="mt-3 space-y-2 text-xs text-slate-600">
                    {[
                      { label: '인식', value: result.agent_reasoning.perceived },
                      { label: '추론', value: result.agent_reasoning.reasoned },
                      { label: '계획', value: result.agent_reasoning.planned },
                      { label: '행동', value: result.agent_reasoning.acted },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex gap-2">
                        <span className="font-bold text-slate-500 w-8 flex-shrink-0">{label}</span>
                        <span className="text-slate-600">{value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* OWL 클래스 */}
              <div className="card">
                <button onClick={() => setShowOntology(!showOntology)}
                  className="flex items-center justify-between w-full text-sm font-semibold text-slate-700 hover:text-teal-600 transition-colors">
                  <span className="flex items-center gap-2"><Award size={14} />활성화된 OWL 클래스</span>
                  {showOntology ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </button>
                {showOntology && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {result.ontology_classes_activated.map(c => (
                      <OntologyTag key={c} className_name={c} />
                    ))}
                  </div>
                )}
              </div>

              <button onClick={() => { setResult(null); setCustomAnswer('') }}
                className="w-full flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-teal-600 border border-slate-200 rounded-xl py-2 hover:border-teal-300 transition-colors">
                <RefreshCw size={13} /> 다른 시나리오 선택
              </button>
            </>
          )}
        </div>
      </div>

      {/* 오개념 빠른 참조 */}
      <div className="card">
        <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
          <Star size={15} className="text-amber-500" /> 순환소수 핵심 오개념 빠른 참조
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { code: 'NM01', title: '순환소수=무한소수', fix: '0.333...은 무한히 반복 → 무한소수', icon: '∞' },
            { code: 'NM03', title: '점 표기: 양 끝에 점', fix: '0.1̇3̇ (1과 3 모두)', icon: '🔴' },
            { code: 'NM05', title: '순환 전 자리 처리', fix: '10x와 100x 두 가지 활용', icon: '✖️' },
            { code: 'NM07', title: '기약분수 먼저!', fix: '3/15 → 1/5 → 유한소수', icon: '✂️' },
            { code: 'NM08', title: '기약분수화 필수', fix: '판별 전 항상 기약분수로', icon: '⚠️' },
          ].map(m => (
            <div key={m.code} className="bg-slate-50 rounded-xl p-3 border border-slate-200">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">{m.icon}</span>
                <span className="text-xs font-mono font-bold text-teal-600">{m.code}</span>
              </div>
              <p className="text-xs font-bold text-slate-700 mb-1">{m.title}</p>
              <p className="text-xs text-slate-500 font-mono">{m.fix}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
